'use client';

import { Component, type ReactNode } from 'react';

/**
 * Catches unrecoverable errors thrown inside `BoardCanvas3D` (shader
 * compile failures, WebGL context loss, drei/three runtime exceptions)
 * and renders the supplied 2D fallback instead. Without this boundary an
 * error inside the R3F tree would unwind to React's root and blank the
 * lobby — the user just sees a dead tab. The 2D fallback uses the same
 * `seats` data so the user can keep playing on whichever device tripped
 * the 3D pipeline.
 *
 * Logs once per mount (subsequent re-renders share the same boundary
 * instance) so we don't spam telemetry on a render-loop crash.
 */
interface Props {
  /** Rendered when the 3D canvas crashes — typically a `<BoardCanvas2D>`
   *  configured with the same `seats` / `gridSize` / handlers. */
  fallback: ReactNode;
  /** Optional hook for telemetry. Receives the original error + React's
   *  componentStack so callers can route to Sentry / DataDog. */
  onError?: (error: Error, info: { componentStack?: string }) => void;
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class CanvasErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // Surface to telemetry if a callback is wired (today nothing wires
    // it, but the indirection means swapping the host's `console.error`
    // for a real sink is a one-line change). The console call below
    // stays as a dev-time backstop so the failure isn't silent.
    this.props.onError?.(error, info);
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(
        '[CanvasErrorBoundary] BoardCanvas3D crashed; falling back to 2D.',
        error,
        info.componentStack,
      );
    }
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}
